import { useState, useEffect, useCallback } from 'react';
import { X, Plus, Shield, Target, User, Check } from 'lucide-react';
import { getUserIdentity, saveUserIdentity, UserIdentity } from '../../services/trainingService';

interface IdentityManagerProps {
    onSave?: () => void;
}

export default function IdentityManager({ onSave }: IdentityManagerProps) {
    const [identity, setIdentity] = useState<UserIdentity | null>(null);
    const [newRole, setNewRole] = useState('');
    const [newVip, setNewVip] = useState('');
    const [showSaved, setShowSaved] = useState(false);

    // Load identity on mount
    useEffect(() => {
        setIdentity(getUserIdentity());
    }, []);

    // Save with toast feedback
    const saveAndNotify = useCallback((updates: Partial<UserIdentity>) => {
        if (!identity) return;

        const updated = { ...identity, ...updates };
        setIdentity(updated);
        saveUserIdentity(updates);

        // Show saved toast
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 2000);

        onSave?.();
    }, [identity, onSave]);

    // Add a new role
    const addRole = () => {
        if (!newRole.trim() || !identity) return;
        const trimmed = newRole.trim();
        if (identity.roles.includes(trimmed)) {
            setNewRole('');
            return;
        }
        saveAndNotify({ roles: [...identity.roles, trimmed] });
        setNewRole('');
    };

    // Remove a role
    const removeRole = (role: string) => {
        if (!identity) return;
        saveAndNotify({ roles: identity.roles.filter(r => r !== role) });
    };

    // Add a new VIP
    const addVip = () => {
        if (!newVip.trim() || !identity) return;
        const trimmed = newVip.trim();
        if (identity.vipEntities.includes(trimmed)) {
            setNewVip('');
            return;
        }
        saveAndNotify({ vipEntities: [...identity.vipEntities, trimmed] });
        setNewVip('');
    };

    // Remove a VIP
    const removeVip = (vip: string) => {
        if (!identity) return;
        saveAndNotify({ vipEntities: identity.vipEntities.filter(v => v !== vip) });
    };

    // Update North Star
    const updateNorthStar = (value: string) => {
        saveAndNotify({ primaryGoal: value });
    };

    if (!identity) {
        return (
            <div className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm animate-pulse">
                <div className="h-6 bg-gray-200 dark:bg-dark-elevated rounded w-1/3 mb-4"></div>
                <div className="h-10 bg-gray-200 dark:bg-dark-elevated rounded"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Saved Toast */}
            {showSaved && (
                <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-[#0D5F6B] text-white px-4 py-2 rounded-xl shadow-lg animate-fade-in">
                    <Check className="w-4 h-4" />
                    <span className="text-sm font-medium">Settings saved</span>
                </div>
            )}

            {/* My Roles Section */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center">
                        <User className="w-5 h-5 text-[#0D5F6B]" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">My Roles</h2>
                        <p className="text-xs text-[#7B8C9B]">How I identify myself</p>
                    </div>
                </div>

                {/* Role Tags */}
                <div className="flex flex-wrap gap-2 mb-3">
                    {identity.roles.map((role) => (
                        <span
                            key={role}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#D9EEF2] text-[#0D5F6B] text-sm font-medium"
                        >
                            {role}
                            <button
                                onClick={() => removeRole(role)}
                                className="ml-1 hover:bg-[#0D5F6B]/10 rounded-full p-0.5"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </span>
                    ))}
                    {identity.roles.length === 0 && (
                        <span className="text-sm text-[#7B8C9B] italic">No roles yet</span>
                    )}
                </div>

                {/* Add Role Input */}
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addRole()}
                        placeholder="Add a role (e.g., Developer, Parent)"
                        className="flex-1 px-3 py-2 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white text-sm placeholder:text-[#7B8C9B]"
                    />
                    <button
                        onClick={addRole}
                        disabled={!newRole.trim()}
                        className="px-3 py-2 rounded-xl bg-[#0D5F6B] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus className="w-5 h-5" />
                    </button>
                </div>
            </section>

            {/* VIP Protocol Section */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">VIP Protocol</h2>
                        <p className="text-xs text-[#7B8C9B]">People & events I never auto-reschedule</p>
                    </div>
                </div>

                {/* VIP Tags */}
                <div className="flex flex-wrap gap-2 mb-3">
                    {identity.vipEntities.map((vip) => (
                        <span
                            key={vip}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-sm font-medium"
                        >
                            🔒 {vip}
                            <button
                                onClick={() => removeVip(vip)}
                                className="ml-1 hover:bg-amber-200 dark:hover:bg-amber-800/50 rounded-full p-0.5"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </span>
                    ))}
                    {identity.vipEntities.length === 0 && (
                        <span className="text-sm text-[#7B8C9B] italic">No VIPs yet</span>
                    )}
                </div>

                {/* Add VIP Input */}
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={newVip}
                        onChange={(e) => setNewVip(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addVip()}
                        placeholder="Add a VIP (e.g., Partner, Boss)"
                        className="flex-1 px-3 py-2 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white text-sm placeholder:text-[#7B8C9B]"
                    />
                    <button
                        onClick={addVip}
                        disabled={!newVip.trim()}
                        className="px-3 py-2 rounded-xl bg-amber-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus className="w-5 h-5" />
                    </button>
                </div>
            </section>

            {/* North Star Section */}
            <section className="bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Target className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                        <h2 className="text-base font-semibold text-[#1A2832] dark:text-white">North Star</h2>
                        <p className="text-xs text-[#7B8C9B]">My primary goal - every decision leads here</p>
                    </div>
                </div>

                <textarea
                    value={identity.primaryGoal}
                    onChange={(e) => updateNorthStar(e.target.value)}
                    placeholder="What's your main focus? (e.g., $550k ARV by Q2, Launch MVP, Get promoted)"
                    rows={3}
                    className="w-full px-3 py-2 rounded-xl border border-[#DCE4EA] dark:border-dark-border bg-white dark:bg-dark-elevated text-[#1A2832] dark:text-white text-sm placeholder:text-[#7B8C9B] resize-none"
                />
            </section>
        </div>
    );
}
